import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Calendar, ArrowRight, Building, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePageContent } from '../hooks/usePageContent';
import FRANCE_REGIONS from '../data/franceRegions';

const API = process.env.REACT_APP_BACKEND_URL;

// Projection: IDF center (319,155) = Paris (48.86°N, 2.35°E)
// scale_x=28.9, scale_y=-79.8
const geoToSvg = (lat, lon) => ({
  x: 319 + (lon - 2.35) * 28.9,
  y: 155 + (48.86 - lat) * 79.8,
});

const REAL_DEFAULTS = {
  hero_title: 'Nos Réalisations',
  hero_subtitle: "Découvrez une sélection de nos projets réalisés en partenariat avec les plus grands acteurs de la construction.",
  featured_label: 'Projets phares',
  featured_title: 'Réalisations emblématiques',
  cta_title: 'Votre projet mérite le meilleur',
  cta_text: "Confiez-nous votre projet et bénéficiez de notre expertise en béton précontraint.",
};

const PageHeader = ({ c }) => (
  <section className="pt-32 pb-20 bg-[#0e1c3f]" data-testid="realisations-header">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl">
        <p className="text-[#0078b7] font-bold tracking-[0.2em] uppercase text-sm mb-4">Nos Références</p>
        <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white uppercase tracking-tight mb-6">
          {c.hero_title}
        </h1>
        <p className="text-slate-300 text-lg leading-relaxed">
          {c.hero_subtitle}
        </p>
      </div>
    </div>
  </section>
);

const FeaturedRealisations = ({ realisations, c }) => {
  if (!realisations.length) return null;

  return (
    <section className="py-24 bg-white" data-testid="featured-realisations">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <p className="text-[#ff65c3] font-bold tracking-[0.2em] uppercase text-sm mb-4">{c.featured_label}</p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0e1c3f] uppercase tracking-tight">
            {c.featured_title}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {realisations.map((r, i) => (
            <div
              key={r.id}
              className={`group relative overflow-hidden ${i === 0 ? 'lg:col-span-2 lg:row-span-2' : ''}`}
              data-testid={`featured-${r.id}`}
            >
              <div className={`relative ${i === 0 ? 'aspect-[16/10]' : 'aspect-[4/3]'} overflow-hidden bg-slate-200`}>
                <img
                  src={r.image_url}
                  alt={r.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0e1c3f]/80 via-[#0e1c3f]/20 to-transparent" />
                <div className="absolute top-4 left-4">
                  <span className="bg-[#ff65c3] text-white text-xs font-bold px-3 py-1 uppercase tracking-wider">
                    À la une
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  {r.location && (
                    <span className="flex items-center gap-1 text-white/70 text-sm mb-2">
                      <MapPin className="h-3.5 w-3.5" />
                      {r.location}
                    </span>
                  )}
                  <h3 className={`font-heading font-bold text-white group-hover:text-[#0078b7] transition-colors ${i === 0 ? 'text-2xl sm:text-3xl' : 'text-xl'}`}>
                    {r.title}
                  </h3>
                  {r.description && i === 0 && (
                    <p className="text-white/70 text-sm mt-2 line-clamp-2">{r.description}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const RealisationsMap = ({ realisations }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const mapRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (mapRef.current) observer.observe(mapRef.current);
    return () => observer.disconnect();
  }, []);

  const points = realisations
    .filter(r => r.lat && r.lon)
    .map(r => ({ ...r, ...geoToSvg(r.lat, r.lon) }));

  const historical = realisations.filter(r => !r.is_featured);

  const categoryLabels = {
    'commercial': 'Commercial',
    'industriel': 'Logistique',
    'residentiel': 'Résidentiel',
    'infrastructure': 'Infrastructure',
  };

  const grouped = {};
  historical.forEach(r => {
    const cat = r.category || 'autre';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(r);
  });

  return (
    <section ref={mapRef} className="py-20 bg-[#0e1c3f] relative overflow-hidden" data-testid="realisations-map-section">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#0078b7] rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#ff65c3] rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div className="mb-10">
        </div>

        {/* Map left + List right */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
          {/* Map - compact */}
          <div className="lg:col-span-2 lg:sticky lg:top-24">
            <svg
              viewBox={FRANCE_REGIONS.viewBox}
              className={`w-full h-auto max-w-sm mx-auto transition-all duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
              data-testid="realisations-france-map"
            >
              <defs>
                <filter id="rGlow">
                  <feGaussianBlur stdDeviation="3" result="blur" />
                  <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
                <filter id="mapShadowR">
                  <feDropShadow dx="0" dy="3" stdDeviation="6" floodColor="#000" floodOpacity="0.25"/>
                </filter>
              </defs>

              <g filter="url(#mapShadowR)">
                {FRANCE_REGIONS.locations.map((region) => (
                  <path
                    key={region.id}
                    d={region.path}
                    fill="#3d5568"
                    stroke="#1a2d45"
                    strokeWidth="1"
                    style={{ opacity: isVisible ? 1 : 0, transition: `opacity 0.5s ease ${Math.random() * 0.4}s` }}
                  />
                ))}
              </g>

              {points.map((p, i) => {
                const isHovered = hoveredId === p.id;
                const isFeatured = p.is_featured;
                return (
                  <g
                    key={p.id || i}
                    onMouseEnter={() => setHoveredId(p.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    className="cursor-pointer"
                    style={{ opacity: isVisible ? 1 : 0, transition: `opacity 0.4s ease ${0.6 + i * 0.03}s` }}
                  >
                    {(isHovered || isFeatured) && (
                      <circle cx={p.x} cy={p.y} r={isHovered ? 12 : 8} fill={isFeatured ? '#ff65c3' : '#0078b7'} opacity={0.2} filter="url(#rGlow)" />
                    )}
                    <circle
                      cx={p.x} cy={p.y}
                      r={isFeatured ? 5 : (isHovered ? 5 : 3)}
                      fill={isFeatured ? '#ff65c3' : '#0078b7'}
                      stroke="white"
                      strokeWidth={isFeatured ? 1.5 : 0.8}
                      opacity={isHovered || isFeatured ? 1 : 0.8}
                      className="transition-all duration-200"
                    />
                    {isHovered && (
                      <g>
                        <rect x={p.x + 8} y={p.y - 14} width={p.title.length * 6 + 16} height="20" rx="3" fill="rgba(14,28,63,0.95)" stroke="#0078b7" strokeWidth="0.5" />
                        <text x={p.x + 16} y={p.y} fill="white" fontSize="9" fontWeight="600" style={{ fontFamily: 'Outfit, sans-serif' }}>{p.title}</text>
                      </g>
                    )}
                  </g>
                );
              })}
            </svg>

            {/* Legend */}
            <div className="flex justify-center gap-6 mt-4">
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-[#ff65c3]" />
                <span className="text-slate-400 text-xs">Projets phares</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-[#0078b7]" />
                <span className="text-slate-400 text-xs">Historiques</span>
              </div>
            </div>
          </div>

          {/* Realisations list - right */}
          <div className="lg:col-span-3 space-y-4" data-testid="historical-realisations">
            <p className="text-white/50 font-bold tracking-[0.15em] uppercase text-xs mb-2">Autres réalisations historiques</p>
            {Object.entries(grouped).map(([cat, items]) => (
              <div key={cat} className="bg-white/[0.04] border border-white/10 overflow-hidden rounded-sm">
                <div className="px-5 py-2.5 border-b border-white/10 bg-white/[0.03]">
                  <h3 className="font-heading font-bold text-[#0078b7] text-xs uppercase tracking-wider">
                    {categoryLabels[cat] || cat}
                  </h3>
                </div>
                <div className="divide-y divide-white/5">
                  {items.map((r) => (
                    <div
                      key={r.id}
                      className="px-5 py-2 flex items-center gap-3 hover:bg-white/[0.03] transition-colors"
                      onMouseEnter={() => setHoveredId(r.id)}
                      onMouseLeave={() => setHoveredId(null)}
                    >
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 transition-colors ${hoveredId === r.id ? 'bg-[#ff65c3]' : 'bg-[#0078b7]'}`} />
                      <p className="font-heading font-semibold text-xs text-white/80 flex-1 truncate">{r.title}</p>
                      {r.location && (
                        <span className="text-white/30 text-xs flex items-center gap-1 flex-shrink-0">
                          <MapPin className="h-3 w-3" />
                          {r.location}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const CTASection = ({ c }) => (
  <section className="py-24 bg-white">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0e1c3f] uppercase tracking-tight mb-6">
        {c.cta_title}
      </h2>
      <p className="text-slate-600 text-lg mb-10 max-w-2xl mx-auto">
        {c.cta_text}
      </p>
      <Button asChild size="lg" className="bg-[#0e1c3f] hover:bg-[#0e1c3f]/90 text-white font-medium px-8 rounded-sm">
        <Link to="/contact">
          Discuter de votre projet
          <ArrowRight className="ml-2 h-5 w-5" />
        </Link>
      </Button>
    </div>
  </section>
);

const RealisationsPage = () => {
  const [realisations, setRealisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const { content: c } = usePageContent('realisations', REAL_DEFAULTS);

  useEffect(() => {
    const fetchRealisations = async () => {
      try {
        const { data } = await axios.get(`${API}/api/realisations`);
        setRealisations(data);
      } catch (e) {
        console.error('Error fetching realisations:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchRealisations();
  }, []);

  const featured = realisations.filter(r => r.is_featured);

  if (loading) {
    return (
      <div data-testid="realisations-page">
        <PageHeader c={c} />
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => <div key={i} className="bg-slate-100 animate-pulse h-[400px]" />)}
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="realisations-page">
      <PageHeader c={c} />
      <FeaturedRealisations realisations={featured} c={c} />
    </div>
  );
};

export default RealisationsPage;
